import { useState, useRef, useEffect, useMemo, memo } from 'react';
import { clsx } from 'clsx';
import {
  Cake,
  Star,
  Sparkles,
  User,
  Clock,
  AlertTriangle,
  ExternalLink,
  Phone,
  Mail,
  Scissors,
  MapPin,
  Calendar,
  MessageSquare,
  Copy,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MoreVertical,
} from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import { WhatsAppMessagePreview } from '@/components/common/WhatsAppMessagePreview';
import { buildWhatsappUrl } from '@/utils/whatsapp';
import { buildCrmTags, interpolateTemplate } from '@/utils/crmMessages';
import { getEligibleCrmTemplates } from '@/utils/crmTemplateEligibility';
import {
  getInitials,
  formatClientTenure,
  getCrmClientBadges,
  formatAmountWithBs,
  type CrmBadgeItem,
} from '@/utils/crmHelpers';
import { useSettingsStore, useCrmStore, useAuthStore } from '@/stores';
import { loadRatesCache } from '@/services/rates.service';
import { formatDateDDMMYY, getDaysUntil, getDaysAbsent } from '@/utils/date';
import { resolveLocationName } from '@/utils/locationResolution';
import type { CrmClient, CrmSubFilter } from '@/types/crm';

export interface CrmClientCardProps {
  client: CrmClient;
  activeSegmentId: string;
  activeSubFilter?: CrmSubFilter;
  isProcessed: boolean;
  onMarkProcessed: () => void;
  onOpenDetails: (client: CrmClient) => void;
}

const BADGE_VARIANTS: Record<string, string> = {
  pink: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  red: 'bg-brand-danger/10 text-brand-danger border-brand-danger/20',
  blue: 'bg-brand-primary/10 text-brand-primary border-brand-primary/20',
  slate: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  neutral: 'bg-surface-tertiary text-text-muted border-border',
};

function renderBadgeIcon(iconName?: string) {
  switch (iconName) {
    case 'cake':
      return <Cake size={11} className="shrink-0" />;
    case 'star':
      return <Star size={11} className="shrink-0" />;
    case 'sparkles':
      return <Sparkles size={11} className="shrink-0" />;
    case 'user':
      return <User size={11} className="shrink-0" />;
    case 'clock':
      return <Clock size={11} className="shrink-0" />;
    case 'alert':
      return <AlertTriangle size={11} className="shrink-0" />;
    case 'calendar':
      return <Calendar size={11} className="shrink-0" />;
    default:
      return null;
  }
}

export function BadgePill({ badge }: { badge: CrmBadgeItem }) {
  const variantClass = BADGE_VARIANTS[badge.variant] || BADGE_VARIANTS.neutral;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0',
        variantClass
      )}
    >
      {renderBadgeIcon(badge.iconName)}
      <span>{badge.label}</span>
    </span>
  );
}

export function CrmSegmentBadge({
  client,
  activeSegmentId,
  daysAbsent,
}: {
  client: CrmClient;
  activeSegmentId: string;
  daysAbsent: number;
}) {
  const segmentMemberships = useCrmStore((s) => s.segmentMemberships);
  const segments = useCrmStore((s) => s.segments);
  const settings = useSettingsStore();
  const cached = useCrmStore((s) => s.statsCache[client.customerId || client.id]);
  const nextAppt = cached?.nextAppointment || client.nextAppointmentDate;
  const clientWithCache = nextAppt ? { ...client, nextAppointmentDate: nextAppt } : client;
  const badges = getCrmClientBadges(
    clientWithCache,
    activeSegmentId,
    daysAbsent,
    segmentMemberships,
    settings,
    segments
  );
  if (!badges.length) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-text-muted border border-border shrink-0">
        <span>Cliente</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-end">
      {badges.map((badge) => (
        <BadgePill key={badge.key} badge={badge} />
      ))}
    </div>
  );
}

export const CrmClientCard = memo(function CrmClientCard({
  client,
  activeSegmentId,
  activeSubFilter = 'all',
  isProcessed,
  onMarkProcessed,
  onOpenDetails,
}: CrmClientCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [copiedType, setCopiedType] = useState<'phone' | 'message' | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const settings = useSettingsStore();
  const locations = useAuthStore((s) => s.locations);
  const cached = useCrmStore((s) => s.statsCache[client.customerId || client.id]);
  const segmentMemberships = useCrmStore((s) => s.segmentMemberships);
  const segments = useCrmStore((s) => s.segments);
  const { showToast } = useToast();

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showMoreMenu]);

  const visits = cached?.appointments ?? client.totalAppointments ?? 0;
  const lastStaff = cached?.staff || client.lastStaffMember;
  const lastAppt = cached?.lastAppointment || client.lastAppointmentDate;
  const lastService = cached?.serviceName || client.lastServiceName;
  const rawLastLoc = cached?.locationName || client.lastLocationName;
  const lastLocId = cached?.lastLocationId || client.lastLocationId;
  const lastLocation = resolveLocationName(
    lastLocId,
    rawLastLoc,
    locations,
    settings.locationAliases,
    settings.locationNameMode
  );

  const nextAppt = cached?.nextAppointment || client.nextAppointmentDate;
  const nextStaff = cached?.nextStaff || client.nextStaffMember;
  const nextService = cached?.nextServiceName || client.nextServiceName;
  const rawNextLoc = cached?.nextLocationName || client.nextLocationName;
  const nextLocId = cached?.nextLocationId || client.nextLocationId;
  const nextLocation = resolveLocationName(
    nextLocId,
    rawNextLoc,
    locations,
    settings.locationAliases,
    settings.locationNameMode
  );
  const daysUntilNext = nextAppt ? getDaysUntil(nextAppt) : (cached?.daysUntilNext ?? client.daysUntilNext);
  const daysAbsent = lastAppt ? getDaysAbsent(lastAppt) : (cached?.daysAbsent ?? client.daysAbsent ?? 0);

  const clientWithCache: CrmClient = useMemo(
    () => ({
      ...client,
      nextAppointmentDate: nextAppt,
      nextStaffMember: nextStaff,
      nextServiceName: nextService,
      nextLocationName: nextLocation,
      daysUntilNext,
    }),
    [client, nextAppt, nextStaff, nextService, nextLocation, daysUntilNext]
  );

  const tenure = formatClientTenure(client.createdAt);
  const badges = getCrmClientBadges(
    clientWithCache,
    activeSegmentId,
    daysAbsent,
    segmentMemberships,
    settings,
    segments
  );

  const cachedRates = loadRatesCache();
  const autoRate = cachedRates.latest[settings.currencyCode || 'USD']?.rate || 0;
  const activeRate = settings.manualRate || autoRate || null;

  const totalSalesFormatted = formatAmountWithBs(
    client.totalSales,
    settings.currencyCode || 'EUR',
    activeRate
  );
  const outstandingFormatted = formatAmountWithBs(
    client.outstandingAmount,
    settings.currencyCode || 'EUR',
    activeRate
  );
  const hasDebt = (client.outstandingAmount || 0) > 0.01;

  const clientPhone = client.mobileNumber || client.phone || '';
  const currentSegmentObj = segments.find((s) => s.id === activeSegmentId);

  const { options: eligibleOptions, defaultKey } = useMemo(
    () =>
      getEligibleCrmTemplates(
        clientWithCache,
        activeSegmentId,
        activeSubFilter,
        settings,
        currentSegmentObj,
        segmentMemberships,
        segments
      ),
    [
      clientWithCache,
      activeSegmentId,
      activeSubFilter,
      settings,
      currentSegmentObj,
      segmentMemberships,
      segments,
    ]
  );

  const activeKey =
    selectedTemplateKey && eligibleOptions.some((opt) => opt.id === selectedTemplateKey)
      ? selectedTemplateKey
      : defaultKey;

  const activeOption = eligibleOptions.find((opt) => opt.id === activeKey);
  const effectiveTemplate = activeOption?.template || settings.templateCrmDefault;
  const message = interpolateTemplate(effectiveTemplate, buildCrmTags(clientWithCache, settings, activeRate || undefined));
  const whatsappUrl = clientPhone ? buildWhatsappUrl(clientPhone, message, settings.whatsappMode) : '';

  const handleCopy = async (text: string, type: 'phone' | 'message', label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedType(type);
      showToast(`${label} copiado al portapapeles`, 'success');
      setTimeout(() => setCopiedType(null), 2000);
    } catch {
      showToast('No se pudo copiar al portapapeles', 'error');
    }
  };

  const hasNextAppt = Boolean(nextAppt);
  const hasLastVisitInfo = Boolean(
    lastAppt || lastStaff || lastService || lastLocation || (!hasNextAppt && daysAbsent > 0)
  );

  return (
    <div
      className={clsx(
        'bg-surface border rounded-xl p-3.5 sm:p-4 transition-all shadow-xs animate-fade-in',
        isProcessed ? 'border-brand-success/40 bg-brand-success/[0.03]' : 'border-border'
      )}
    >
      {/* 1. Header Row */}
      <div className="flex items-start justify-between gap-2.5 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Avatar */}
          <div
            className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary font-bold flex items-center justify-center text-xs sm:text-sm shrink-0 select-none border border-brand-primary/20"
            aria-hidden="true"
          >
            {getInitials(client.name)}
          </div>

          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => onOpenDetails(client)}
              className="font-bold text-text-primary text-sm sm:text-base truncate hover:text-brand-primary cursor-pointer text-left transition-colors flex items-center gap-1.5 max-w-full"
            >
              <span className="truncate">{client.name}</span>
              <ExternalLink size={13} className="text-text-muted opacity-60 shrink-0" />
            </button>
            {tenure && (
              <p className="text-xs text-text-muted mt-0.5 truncate">{tenure}</p>
            )}
          </div>
        </div>

        {/* Dynamic Badges */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[50%]">
          {badges.map((badge) => (
            <BadgePill key={badge.key} badge={badge} />
          ))}
        </div>
      </div>

      {/* 2. Contextual Next Appointment Block (Green/Blue) */}
      {hasNextAppt && (
        <div className="p-2.5 rounded-lg bg-brand-primary/10 border border-brand-primary/20 text-xs space-y-1 mb-3">
          <div className="flex items-center justify-between gap-1.5 font-medium text-brand-primary dark:text-sky-300">
            <div className="flex items-center gap-1.5 min-w-0">
              <Calendar size={12} className="text-brand-primary dark:text-sky-400 shrink-0" />
              <span className="truncate">
                Próxima cita:{' '}
                <span className="font-semibold">{formatDateDDMMYY(nextAppt)}</span>
                {daysUntilNext !== undefined && daysUntilNext !== null && (
                  <span className="text-text-muted text-xs ml-1 font-normal">
                    ({daysUntilNext === 0 ? 'hoy' : daysUntilNext === 1 ? 'mañana' : `en ${daysUntilNext} días`})
                  </span>
                )}
              </span>
            </div>
            {nextLocation && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-primary/15 text-[11px] font-medium text-brand-primary dark:text-sky-300 shrink-0">
                <MapPin size={10} />
                {nextLocation}
              </span>
            )}
          </div>
          {(nextService || nextStaff) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-text-secondary pt-0.5">
              {nextService && (
                <div className="flex items-center gap-1.5 truncate">
                  <Scissors size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Servicio:</span> {nextService}
                  </span>
                </div>
              )}
              {nextStaff && (
                <div className="flex items-center gap-1.5 truncate">
                  <User size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Atendido por:</span> {nextStaff}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Contextual Last Visit Block (Slate) */}
      {hasLastVisitInfo && (
        <div className="p-2.5 rounded-lg bg-surface-secondary/70 border border-border/80 text-xs space-y-1 mb-3">
          <div className="flex items-center justify-between gap-1.5 font-medium text-text-primary">
            <div className="flex items-center gap-1.5 min-w-0">
              <Clock size={12} className="text-text-muted shrink-0" />
              <span className="truncate">
                Última visita:{' '}
                <span className="font-semibold">{lastAppt ? formatDateDDMMYY(lastAppt) : 'Registrada'}</span>
                {daysAbsent !== undefined && daysAbsent !== null && (
                  <span className="text-text-muted text-xs ml-1 font-normal">
                    ({daysAbsent > 0 ? `hace ${daysAbsent} días` : 'Reciente'})
                  </span>
                )}
              </span>
            </div>
            {lastLocation && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface border border-border text-[11px] text-text-muted shrink-0">
                <MapPin size={10} />
                {lastLocation}
              </span>
            )}
          </div>
          {(lastService || lastStaff) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-text-secondary pt-0.5">
              {lastService && (
                <div className="flex items-center gap-1.5 truncate">
                  <Scissors size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Servicio:</span> {lastService}
                  </span>
                </div>
              )}
              {lastStaff && (
                <div className="flex items-center gap-1.5 truncate">
                  <User size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Atendido por:</span> {lastStaff}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Metrics Grid with Dual-Currency */}
      <div className="grid grid-cols-3 gap-2 p-2 bg-surface-secondary border border-border rounded-xl text-center text-xs mb-3">
        <div className="flex flex-col justify-center py-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Visitas
          </span>
          <span className="font-bold text-text-primary text-sm mt-0.5">{visits}</span>
        </div>
        <div className="flex flex-col justify-center py-1 border-x border-border/60 px-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Total Gastado
          </span>
          <span className="font-bold text-text-primary text-sm mt-0.5">
            {totalSalesFormatted.original}
          </span>
          {totalSalesFormatted.bs && (
            <span className="text-xs text-text-muted font-medium mt-0.5 leading-tight">
              ({totalSalesFormatted.bs})
            </span>
          )}
        </div>
        <div className="flex flex-col justify-center py-1 px-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Deuda
          </span>
          <span
            className={clsx(
              'font-bold text-sm mt-0.5',
              hasDebt ? 'text-brand-danger' : 'text-text-secondary'
            )}
          >
            {outstandingFormatted.original}
          </span>
          {outstandingFormatted.bs && (
            <span
              className={clsx(
                'text-xs font-medium mt-0.5 leading-tight',
                hasDebt ? 'text-brand-danger/90' : 'text-text-muted'
              )}
            >
              ({outstandingFormatted.bs})
            </span>
          )}
        </div>
      </div>

      {/* 4. Contact Info Row */}
      {(clientPhone || client.email) && (
        <div className="flex items-center gap-3 py-1.5 px-2.5 bg-surface-secondary/40 border border-border/50 rounded-lg text-xs text-text-secondary mb-3 flex-wrap">
          {clientPhone && (
            <div className="flex items-center gap-1.5">
              <Phone size={12} className="text-text-muted shrink-0" />
              <a
                href={`tel:${clientPhone}`}
                className="hover:text-brand-primary font-mono text-xs transition-colors"
              >
                {clientPhone}
              </a>
            </div>
          )}
          {clientPhone && client.email && (
            <span className="text-border select-none">•</span>
          )}
          {client.email && (
            <div className="flex items-center gap-1.5 truncate">
              <Mail size={12} className="text-text-muted shrink-0" />
              <a
                href={`mailto:${client.email}`}
                className="hover:text-brand-primary truncate text-xs transition-colors"
              >
                {client.email}
              </a>
            </div>
          )}
        </div>
      )}

      {/* 5. Action Toolbar */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {whatsappUrl && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366] hover:bg-[#25D366]/25 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
            title="Abrir chat en WhatsApp"
          >
            <MessageSquare size={14} className="shrink-0" />
            <span>WhatsApp</span>
          </a>
        )}

        <button
          type="button"
          onClick={() => onOpenDetails(client)}
          className="inline-flex items-center justify-center gap-1.5 px-2.5 min-h-[36px] bg-surface-secondary text-text-secondary hover:bg-surface-tertiary hover:text-text-primary rounded-lg text-xs font-medium transition-colors cursor-pointer"
          title="Ver ficha del cliente"
        >
          <ExternalLink size={13} className="shrink-0" />
          <span>Ficha</span>
        </button>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={clsx(
            'inline-flex items-center justify-center gap-1 px-2.5 min-h-[36px] rounded-lg text-xs font-medium transition-colors cursor-pointer',
            expanded
              ? 'bg-brand-primary/15 text-brand-primary'
              : 'bg-surface-secondary text-text-muted hover:text-text-primary hover:bg-surface-tertiary'
          )}
          title={expanded ? 'Ocultar mensaje sugerido' : 'Ver mensaje sugerido'}
        >
          <MessageSquare size={13} className="shrink-0" />
          <span className="hidden xs:inline sm:inline">Mensaje</span>
          {expanded ? <ChevronUp size={13} className="shrink-0" /> : <ChevronDown size={13} className="shrink-0" />}
        </button>

        {(clientPhone || message) && (
          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              onClick={() => setShowMoreMenu((prev) => !prev)}
              className={clsx(
                'inline-flex items-center justify-center w-9 h-9 min-h-[36px] rounded-lg transition-colors cursor-pointer',
                showMoreMenu
                  ? 'bg-surface-tertiary text-text-primary ring-1 ring-border'
                  : 'bg-surface-secondary text-text-muted hover:text-text-primary hover:bg-surface-tertiary'
              )}
              title="Más opciones (Copiar)"
              aria-label="Más opciones"
              aria-expanded={showMoreMenu}
            >
              <MoreVertical size={15} />
            </button>

            {showMoreMenu && (
              <div className="absolute left-0 bottom-full mb-1.5 z-20 min-w-[170px] bg-surface border border-border rounded-xl shadow-lg p-1 animate-fade-in space-y-0.5">
                {clientPhone && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCopy(clientPhone, 'phone', 'Teléfono');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors text-left cursor-pointer min-h-[36px]"
                  >
                    {copiedType === 'phone' ? (
                      <Check size={14} className="text-brand-success shrink-0" />
                    ) : (
                      <Phone size={14} className="text-text-muted shrink-0" />
                    )}
                    <span>Copiar Teléfono</span>
                  </button>
                )}

                {message && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCopy(message, 'message', 'Mensaje');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors text-left cursor-pointer min-h-[36px]"
                  >
                    {copiedType === 'message' ? (
                      <Check size={14} className="text-brand-success shrink-0" />
                    ) : (
                      <Copy size={14} className="text-text-muted shrink-0" />
                    )}
                    <span>Copiar Mensaje</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onMarkProcessed}
          className={clsx(
            'ml-auto inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] rounded-lg text-xs font-medium transition-colors cursor-pointer shrink-0',
            isProcessed
              ? 'bg-brand-success/15 text-brand-success font-semibold border border-brand-success/30'
              : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary border border-border'
          )}
          title={isProcessed ? 'Marcar como pendiente' : 'Marcar como procesado'}
        >
          <CheckCircle2 size={14} className="shrink-0" />
          <span>{isProcessed ? 'Procesado' : 'Marcar'}</span>
        </button>
      </div>

      {/* 6. Expandable Message Preview */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs text-text-secondary animate-fade-in">
          {eligibleOptions.length > 1 && (
            <div className="space-y-1.5 pb-2">
              <span className="text-[10px] uppercase font-semibold text-text-muted tracking-wider block">
                Plantilla de Mensaje ({eligibleOptions.length} disponibles)
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {eligibleOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setSelectedTemplateKey(opt.id)}
                    className={clsx(
                      'text-xs px-2.5 py-1 rounded-lg border font-medium transition-all cursor-pointer flex items-center gap-1 min-h-[28px]',
                      activeKey === opt.id
                        ? 'bg-brand-primary text-white border-brand-primary shadow-2xs font-semibold'
                        : 'bg-surface-secondary text-text-secondary border-border hover:bg-surface-tertiary hover:text-text-primary'
                    )}
                  >
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-text-muted">
            <span className="font-semibold text-text-primary flex items-center gap-1.5">
              <MessageSquare size={13} className="text-brand-primary" />
              Mensaje contextual sugerido:
            </span>
            {message && (
              <button
                type="button"
                onClick={() => handleCopy(message, 'message', 'Mensaje')}
                className="text-brand-primary hover:underline text-[11px] font-medium flex items-center gap-1 cursor-pointer"
              >
                {copiedType === 'message' ? <Check size={11} /> : <Copy size={11} />}
                <span>Copiar</span>
              </button>
            )}
          </div>
          {message ? (
            <WhatsAppMessagePreview
              message={message}
              variant="bubble"
            />
          ) : (
            <p className="text-text-muted italic">No hay mensaje generado para este cliente.</p>
          )}
        </div>
      )}
    </div>
  );
});
